import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { FileDropZone } from "../../components/FileDropZone";
import { PortalPage, Section, Field } from "../../components/portal/PortalPage";
import { PropertyPicker } from "../../components/portal/PropertyPicker";
import RepeatFaultBanner, { splitFlatAddress } from "../../components/RepeatFaultBanner";
import { OnBehalfSelect } from "../../components/portal/FamilyFilter";
import { CATEGORIES, URGENCY, titleCase } from "../../components/portal/portalUtils";
import { Lightning, ShieldCheck, Camera } from "@phosphor-icons/react";
import { toast } from "sonner";

import DateInput from "../../components/DateInput";
const EMPTY = {
  title: "", request_details: "", category: "general", priority: "medium", requested_completion_date: "",
  property_id: "", property_address: "", street_address: "", town_city: "", postcode: "", what3words: "",
  site_contact_name: "", site_contact_phone: "", price_mandate: false, mandate_limit: "", mandate_note: "",
  urgency: "urgent", access_instructions: "", attachments: [], on_behalf_client_id: "",
};

export default function ClientRequestFormPage({ mode = "request" }) {
  const emergency = mode === "emergency";
  const [params] = useSearchParams();
  const [f, setF] = useState({ ...EMPTY, on_behalf_client_id: params.get("for") || "" });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const manual = !f.property_id;

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error(emergency ? "Tell us what has happened" : "Give your request a title");
    if (manual && (emergency ? !(f.street_address && f.town_city && f.postcode) : !f.property_address.trim())) return toast.error("Enter the property address or pick a saved property");
    if (!emergency && f.price_mandate && !(parseFloat(f.mandate_limit) > 0)) return toast.error("Enter the maximum cost you're authorising");
    setSaving(true);
    try {
      const payload = emergency
        ? { title: f.title, description: f.request_details, urgency: f.urgency, category: f.category, property_id: f.property_id, street_address: f.street_address, town_city: f.town_city, postcode: f.postcode, what3words: f.what3words, site_contact_name: f.site_contact_name, site_contact_phone: f.site_contact_phone, access_instructions: f.access_instructions, attachments: f.attachments, on_behalf_client_id: f.on_behalf_client_id }
        : { ...f, mandate_limit: f.price_mandate ? parseFloat(f.mandate_limit) : 0 };
      const { data } = await API.post(emergency ? "/client-hub/emergencies" : "/client-hub/requests", payload);
      toast.success(emergency ? `Emergency ${data.reference} reported — our team has been alerted` : `Request ${data.reference} submitted`);
      navigate(emergency ? `/portal/emergencies/${data.id}` : `/portal/requests/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not submit");
    } finally { setSaving(false); }
  };

  return (
    <PortalPage
      title={emergency ? "Report an Emergency" : "New Job Request"}
      subtitle={emergency ? "For urgent issues that need a rapid response — leaks, no heating, electrical faults, security breaches." : "Tell us what you need done. We'll acknowledge it immediately and keep you updated at every step."}
      back={{ to: emergency ? "/portal/emergencies" : "/portal/requests", label: emergency ? "Emergencies" : "Requests" }}
      testid={emergency ? "emergency-form-page" : "request-form-page"}
    >
      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {emergency && (
            <div className="border border-red-300 dark:border-red-900 bg-red-50 dark:bg-red-950/30 rounded-lg p-4 flex gap-3" data-testid="emergency-warning">
              <Lightning size={22} className="text-red-600 shrink-0" weight="fill" />
              <p className="text-xs text-red-800 dark:text-red-300">If there is immediate danger to life, call <strong>999</strong> first. For a gas smell call the National Gas Emergency line on <strong>0800 111 999</strong>. Then report here so we can dispatch a contractor.</p>
            </div>
          )}
          <Section title={emergency ? "What has happened?" : "What do you need?"}>
            <div className="space-y-4">
              <Field label={emergency ? "Summary *" : "Title *"}>
                <Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder={emergency ? "e.g. Burst pipe flooding ground floor" : "e.g. Replace broken fence panels at rear"} data-testid="form-title" />
              </Field>
              <Field label="Details" hint="The more detail you give, the faster we can act — where exactly, when it started, anything already tried.">
                <Textarea rows={5} value={f.request_details} onChange={(e) => set("request_details", e.target.value)} data-testid="form-details" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Category">
                  <Select value={f.category} onValueChange={(v) => set("category", v)}>
                    <SelectTrigger data-testid="form-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{titleCase(c)}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                {emergency ? (
                  <Field label="Urgency">
                    <Select value={f.urgency} onValueChange={(v) => set("urgency", v)}>
                      <SelectTrigger data-testid="form-urgency"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(URGENCY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label} — {v.hours}h response</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                ) : (
                  <Field label="Priority">
                    <Select value={f.priority} onValueChange={(v) => set("priority", v)}>
                      <SelectTrigger data-testid="form-priority"><SelectValue /></SelectTrigger>
                      <SelectContent>{["low", "medium", "high", "urgent"].map((p) => <SelectItem key={p} value={p}>{titleCase(p)}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                )}
              </div>
              {!emergency && (
                <Field label="Ideal completion date">
                  <DateInput value={f.requested_completion_date} onChange={(e) => set("requested_completion_date", e.target.value)} className="sm:w-56" data-testid="form-completion-date" />
                </Field>
              )}
            </div>
          </Section>

          <Section title="Where?">
            <div className="space-y-4">
              <OnBehalfSelect value={f.on_behalf_client_id} onChange={(v) => setF((p) => ({ ...p, on_behalf_client_id: v, property_id: "" }))} testid="form-on-behalf" />
              <Field label="Property">
                <PropertyPicker value={f.property_id} clientId={f.on_behalf_client_id} onChange={(id, prop) => setF((p) => ({ ...p, property_id: id, on_behalf_client_id: prop?.client_id && prop.for_client ? prop.client_id : p.on_behalf_client_id }))} />
              </Field>
              <RepeatFaultBanner endpoint="/client-hub/repeat-check" category={f.category} propertyId={f.property_id}
                addressLine={f.street_address || splitFlatAddress(f.property_address).address_line} postcode={f.postcode || splitFlatAddress(f.property_address).postcode}
                linkBase="/portal" testid="form-repeat-fault-banner" />
              {manual && (emergency ? (
                <div className="grid sm:grid-cols-3 gap-3">
                  <Field label="Street address *"><Input value={f.street_address} onChange={(e) => set("street_address", e.target.value)} data-testid="form-street" /></Field>
                  <Field label="Town / City *"><Input value={f.town_city} onChange={(e) => set("town_city", e.target.value)} data-testid="form-town" /></Field>
                  <Field label="Postcode *"><Input value={f.postcode} onChange={(e) => set("postcode", e.target.value.toUpperCase())} data-testid="form-postcode" /></Field>
                </div>
              ) : (
                <Field label="Property address *"><Input value={f.property_address} onChange={(e) => set("property_address", e.target.value)} placeholder="Full address including postcode" data-testid="form-address" /></Field>
              ))}
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="On-site contact name"><Input value={f.site_contact_name} onChange={(e) => set("site_contact_name", e.target.value)} data-testid="form-site-name" /></Field>
                <Field label="On-site contact phone"><Input value={f.site_contact_phone} onChange={(e) => set("site_contact_phone", e.target.value)} data-testid="form-site-phone" /></Field>
              </div>
              {emergency && <Field label="Access instructions"><Textarea rows={2} value={f.access_instructions} onChange={(e) => set("access_instructions", e.target.value)} placeholder="Key safe code, parking, who to ask for…" data-testid="form-access" /></Field>}
            </div>
          </Section>

          <Section title={<span className="flex items-center gap-1.5"><Camera size={14} /> Photos & Files</span>}>
            <p className="text-xs text-muted-foreground mb-3">Add photos of the issue, plans, or anything that helps. Up to 10 files, 20MB each.</p>
            <FileDropZone files={f.attachments} onFilesChange={(files) => set("attachments", files)} folder="client-requests" maxFiles={10} />
          </Section>
        </div>

        <div className="space-y-6">
          {!emergency && (
            <Section title={<span className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-emerald-600" /> Price Mandate</span>} testid="mandate-section">
              <div className="flex items-start justify-between gap-3">
                <p className="text-xs text-muted-foreground">Authorise a maximum spend so we can proceed <strong>without waiting for quotes</strong>. Ideal for routine or low-value work.</p>
                <Switch checked={f.price_mandate} onCheckedChange={(v) => set("price_mandate", v)} data-testid="mandate-switch" />
              </div>
              {f.price_mandate && (
                <div className="mt-4 space-y-3 animate-fade-in">
                  <Field label="Maximum cost (£, ex VAT) *">
                    <Input type="number" min="1" step="0.01" value={f.mandate_limit} onChange={(e) => set("mandate_limit", e.target.value)} placeholder="e.g. 500" data-testid="mandate-limit" />
                  </Field>
                  <Field label="Conditions (optional)">
                    <Textarea rows={2} value={f.mandate_note} onChange={(e) => set("mandate_note", e.target.value)} placeholder="e.g. Use our usual contractor if available" data-testid="mandate-note" />
                  </Field>
                  <p className="text-[11px] text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 rounded p-2">If the work is likely to exceed your limit we'll pause and send you a quote to approve first.</p>
                </div>
              )}
            </Section>
          )}
          <div className="border border-border rounded-lg bg-card p-4 space-y-3">
            <Button type="submit" className="w-full" variant={emergency ? "destructive" : "default"} disabled={saving} data-testid="form-submit">
              {saving ? "Submitting…" : emergency ? "Report Emergency Now" : "Submit Request"}
            </Button>
            <Button type="button" variant="ghost" className="w-full" onClick={() => navigate(-1)} data-testid="form-cancel">Cancel</Button>
            <p className="text-[11px] text-muted-foreground text-center">{emergency ? "Our team is alerted instantly by email and in-app." : "You'll receive an acknowledgement and can track progress here."}</p>
          </div>
        </div>
      </form>
    </PortalPage>
  );
}
